import React from 'react';
import { TrendingUp, BarChart3, PieChart, Activity, MapPin, Calendar, DollarSign, Users } from 'lucide-react';

const Analytics = () => {
  const metrics = [
    {
      title: 'Total Deliveries',
      value: '1,247',
      change: '+12%',
      changeType: 'positive',
      icon: BarChart3,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Completed',
      value: '1,158',
      change: '+8%',
      changeType: 'positive',
      icon: TrendingUp,
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'In Transit',
      value: '89',
      change: '+5%',
      changeType: 'positive',
      icon: Activity,
      color: 'from-primary-500 to-primary-600'
    },
    {
      title: 'Delayed',
      value: '12',
      change: '-3%',
      changeType: 'negative',
      icon: Calendar,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const performanceMetrics = [
    {
      title: 'On-Time Delivery',
      value: '94.2%',
      target: '95%',
      status: 'good'
    },
    {
      title: 'Average Delivery Time',
      value: '2.3 days',
      target: '2.5 days',
      status: 'excellent'
    },
    {
      title: 'Customer Satisfaction',
      value: '4.8/5',
      target: '4.5/5',
      status: 'excellent'
    },
    {
      title: 'Cost per Delivery',
      value: '$45.20',
      target: '$50.00',
      status: 'good'
    }
  ];

  const topRoutes = [
    {
      route: 'New York → Boston',
      deliveries: 156,
      revenue: '$7,800',
      efficiency: '96%'
    },
    {
      route: 'Los Angeles → San Diego',
      deliveries: 134,
      revenue: '$6,700',
      efficiency: '94%'
    },
    {
      route: 'Chicago → Detroit',
      deliveries: 98,
      revenue: '$4,900',
      efficiency: '92%'
    },
    {
      route: 'Miami → Orlando',
      deliveries: 87,
      revenue: '$4,350',
      efficiency: '89%'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600 bg-green-100';
      case 'good':
        return 'text-primary-600 bg-primary-100';
      case 'warning':
        return 'text-orange-600 bg-orange-100';
      case 'poor':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="gradient-bg page-container">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reports & Analytics</h1>
          <p className="text-gray-600">Comprehensive insights into your delivery performance</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="gradient-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${
                        metric.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {metric.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">from last month</span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 bg-gradient-to-r ${metric.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Revenue Trends Chart */}
          <div className="lg:col-span-2 gradient-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Revenue Trends</h2>
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-primary-600" />
                <span className="text-sm text-primary-600 font-medium">+15% this month</span>
              </div>
            </div>
            <div className="h-64 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                <p className="text-primary-600 font-medium">Chart Placeholder</p>
                <p className="text-sm text-primary-500">Revenue trends over time</p>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="gradient-card">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Performance Metrics</h2>
            <div className="space-y-4">
              {performanceMetrics.map((metric, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{metric.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(metric.status)}`}>
                      {metric.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-gray-900">{metric.value}</span>
                    <span className="text-sm text-gray-500">Target: {metric.target}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Delivery Routes */}
        <div className="gradient-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Top Delivery Routes</h2>
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-primary-600" />
              <span className="text-sm text-primary-600 font-medium">Most efficient routes</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Route</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Deliveries</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Revenue</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Efficiency</th>
                </tr>
              </thead>
              <tbody>
                {topRoutes.map((route, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{route.route}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">{route.deliveries}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">{route.revenue}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        {route.efficiency}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Additional Analytics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {/* Customer Satisfaction */}
          <div className="gradient-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Customer Satisfaction</h3>
              <Users className="w-5 h-5 text-primary-600" />
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">4.8/5</div>
              <p className="text-sm text-gray-600">Average rating from 1,247 reviews</p>
              <div className="flex justify-center mt-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <div key={star} className="w-4 h-4 bg-yellow-400 rounded-full mx-1"></div>
                ))}
              </div>
            </div>
          </div>

          {/* Delivery Distribution */}
          <div className="gradient-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Delivery Distribution</h3>
              <PieChart className="w-5 h-5 text-primary-600" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Same Day</span>
                <span className="text-sm font-medium text-gray-900">15%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Next Day</span>
                <span className="text-sm font-medium text-gray-900">45%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">2-3 Days</span>
                <span className="text-sm font-medium text-gray-900">30%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">4+ Days</span>
                <span className="text-sm font-medium text-gray-900">10%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 